// src/screens/AttendanceAnalytics.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useSearchParams } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Users, CalendarDays, FileDown } from "lucide-react";
import { getISOWeek, getISOWeekYear, getISODay, format } from "date-fns";
import { fr } from "date-fns/locale";
import jsPDF from "jspdf";
import { useTheme } from "../context/ThemeProvider";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";
import { BackButton } from "../components/BackButton";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";


/* ================= i18n mini-hook (safe) ================= */
function useI18nSafe() {
  const [, setTick] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setTick(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, opts?: Record<string, unknown>): string =>
    opts ? String((i18n.t as any)(key, opts)) : String((i18n.t as any)(key));
  return { t };
}

/* ================= Types ================= */
type Player = {
  id?: string;
  firstName?: string;
  lastName?: string;
  number?: number;
  position?: string;
};
type Team = { id: string; team_name: string; players?: Player[] };
type Training = {
  id: string;
  date: string;
  user_id: string;
  team_id: string;
  notes?: string | null;
};
type Attendance = { training_id: string; player_id: string; was_present: boolean };

/* ================= Constantes ================= */
const COLORS = { present: "#16a34a", absent: "#94a3b8" };
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function AttendanceAnalytics() {
  const { t } = useI18nSafe();
  const [search] = useSearchParams();
  const seedTeamId = search.get("team_id") || "";

  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(seedTeamId);
  const [players, setPlayers] = useState<Player[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtres de dates (optionnels)
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const rosterSize = useMemo(
    () => players.filter((p) => !!p?.id).length,
    [players]
  );

  /* ================= Utils dates ================= */
  const buildStart = (s: string) => {
    if (!s) return null;
    const d = new Date(s + "T00:00:00");
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  };
  const buildEnd = (s: string) => {
    if (!s) return null;
    const d = new Date(s + "T00:00:00");
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      23,
      59,
      59,
      999
    );
  };
  const startBound = useMemo(() => buildStart(dateStart), [dateStart]);
  const endBound = useMemo(() => buildEnd(dateEnd), [dateEnd]);

  /* ================= Sessions calculées ================= */
  const sessions = useMemo(() => {
    return trainings.map((t) => {
      const d = new Date(t.date);
      const isoWeek = getISOWeek(d);
      const isoYear = getISOWeekYear(d);
      const weekday = getISODay(d) - 1; // 0..6, Lundi=0
      const present = attendance.filter(
        (a) => a.training_id === t.id && a.was_present
      ).length;
      const ratio = rosterSize > 0 ? present / rosterSize : 0;
      return { id: t.id, date: d, isoWeek, isoYear, weekday, ratio };
    });
  }, [trainings, attendance, rosterSize]);

  function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function safeFileName(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // enlève accents
    .replace(/[^a-zA-Z0-9-_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}


  const weeks = useMemo(() => {
    const uniq = new Map<
      string,
      { key: string; isoYear: number; isoWeek: number }
    >();
    sessions.forEach((s) => {
      const key = `${s.isoYear}-${String(s.isoWeek).padStart(2, "0")}`;
      if (!uniq.has(key))
        uniq.set(key, { key, isoYear: s.isoYear, isoWeek: s.isoWeek });
    });
    return Array.from(uniq.values()).sort(
      (a, b) => a.isoYear - b.isoYear || a.isoWeek - b.isoWeek
    );
  }, [sessions]);

  type CellAgg = { sum: number; count: number; date: Date | null };
  const heatCells = useMemo(() => {
    const map = new Map<string, CellAgg>();
    sessions.forEach((s) => {
      const colKey = `${s.isoYear}-${String(s.isoWeek).padStart(2, "0")}`;
      const key = `${colKey}-${s.weekday}`;
      const prev = map.get(key) || { sum: 0, count: 0, date: null };
      map.set(key, {
        sum: prev.sum + s.ratio,
        count: prev.count + 1,
        date: s.date,
      });
    });

    return weeks.map((w, colIdx) =>
      Array.from({ length: 7 }, (_, weekday) => {
        const key = `${w.key}-${weekday}`;
        const cell = map.get(key);
        const value = cell ? cell.sum / cell.count : null;
        const dateLabel = cell?.date
          ? format(cell.date, "EEE d MMM yyyy", { locale: fr })
          : "";
        return {
          colIdx,
          weekday,
          value,
          dateLabel,
          weekLabel: `S${w.isoWeek}`,
        };
      })
    );
  }, [sessions, weeks]);

  const valueToColor = (v: number | null) => {
    if (v === null) return isDark ? "#111827" : "#e5e7eb";
    const h = Math.round(130 * v);
    const s = 70;
    const l = Math.round(95 - 60 * v);
    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  const teamName = useMemo(
    () =>
      teams.find((t) => t.id === selectedTeamId)?.team_name ||
      t("attendance:title.teamDefault"),
    [teams, selectedTeamId, t]
  );

  /* ================= Export PDF ================= */
const handleExportSummaryPdf = async () => {
  try {
    if (!perPlayer || perPlayer.length === 0) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 40;
    const maxW = pageW - margin * 2;

    const wPresent = 80;
    const wAbsent = 80;
    const wRate = 90;
    const wName = maxW - (wPresent + wAbsent + wRate);
    const rowH = 20;

    let y = margin;

    const drawHeader = () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(t("attendance:pdf.title"), margin, y);
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      const period =
        dateStart || dateEnd
          ? `${t("attendance:pdf.period")}: ${dateStart || "…"} → ${dateEnd || "…"}`
          : "";

      const line2 = `${teamName} — ${trainings.length} ${t("attendance:pdf.trainings")}`;

      if (period) {
        doc.text(period, margin, y);
        y += 14;
      }
      doc.text(line2, margin, y);
      y += 18;

      doc.setFillColor(240, 240, 240);
      doc.rect(margin, y - 14, maxW, 24, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(t("attendance:table.player"), margin + 6, y);
      doc.text(t("attendance:table.present"), margin + wName + 6, y);
      doc.text(t("attendance:table.absent"), margin + wName + wPresent + 6, y);
      doc.text(
        t("attendance:table.rate"),
        margin + wName + wPresent + wAbsent + 6,
        y
      );
    };

    const ensurePage = (need: number) => {
      if (y + need > pageH - margin) {
        doc.addPage();
        y = margin;
        drawHeader();
      }
    };

    const drawRow = (name: string, present: number, absent: number, ratePct: string) => {
      y += rowH;
      ensurePage(rowH);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      const nameMaxChars = 45;
      const safeNameTxt =
        name.length > nameMaxChars ? name.slice(0, nameMaxChars - 1) + "…" : name;

      doc.text(safeNameTxt, margin + 6, y);
      doc.text(String(present), margin + wName + wPresent - 6, y, { align: "right" });
      doc.text(
        String(absent),
        margin + wName + wPresent + wAbsent - 6,
        y,
        { align: "right" }
      );
      doc.text(
        ratePct,
        margin + wName + wPresent + wAbsent + wRate - 6,
        y,
        { align: "right" }
      );

      doc.setDrawColor(230, 230, 230);
      doc.line(margin, y + 6, margin + maxW, y + 6);
    };

    drawHeader();
    perPlayer.forEach((p) => {
      const ratePct = `${(p.rate * 100).toFixed(0)}%`;
      drawRow(p.name, p.present, p.absent, ratePct);
    });

    y += 24;
    ensurePage(0);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `${t("attendance:pdf.generated")} ${new Date().toLocaleDateString("fr-FR")} — ${teamName}`,
      margin,
      Math.min(y, pageH - margin)
    );

    const sanitizedTeam = safeFileName(teamName || "team");
    const fileName = `presence_synthese_${sanitizedTeam}.pdf`;

    // ✅ WEB
    if (!Capacitor.isNativePlatform()) {
      doc.save(fileName);
      return;
    }

    // ✅ MOBILE (Capacitor): write + share/open
    await Filesystem.requestPermissions();

    const arrayBuffer = doc.output("arraybuffer");
    const base64 = arrayBufferToBase64(arrayBuffer);

    const writeRes = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });

    // writeRes.uri est généralement un content:// (Android) ou file:// (iOS)
    await Share.share({
      title: fileName,
      text: t("attendance:summary.exportPdf"),
      url: writeRes.uri,
      dialogTitle: t("attendance:summary.export"),
    });
  } catch (e) {
    console.error("Export PDF failed:", e);
  }
};


  /* ================= 1) Charger les équipes ================= */
  useEffect(() => {
    (async () => {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name, players")
        .eq("user_id", user.id)
        .or("archived.is.null,archived.eq.false")
        .order("team_name", { ascending: true });

      if (error) {
        console.error("Erreur chargement équipes:", error);
        setLoading(false);
        return;
      }
      const list = data || [];
      setTeams(list);
      if (!seedTeamId && list.length > 0) setSelectedTeamId(list[0].id);
      setLoading(false);
    })();
  }, [seedTeamId]);

  /* ================= 2) Charger joueurs + trainings + attendance ================= */
  useEffect(() => {
    (async () => {
      if (!selectedTeamId) return;
      setLoading(true);

      // joueurs
      const teamRow = teams.find((t) => t.id === selectedTeamId);
      if (!teamRow?.players) {
        const { data: tRow } = await supabase
          .from("teams")
          .select("players")
          .eq("id", selectedTeamId)
          .maybeSingle();
        setPlayers(
          (tRow?.players || []).filter((p: Player) => !!p?.id)
        );
      } else {
        setPlayers(
          teamRow.players.filter((p: Player) => !!p?.id)
        );
      }

      // trainings
      const { data: trData, error: trErr } = await supabase
        .from("trainings")
        .select("*")
        .eq("team_id", selectedTeamId)
        .order("date", { ascending: false });

      if (trErr) {
        console.error("Erreur chargement trainings:", trErr);
        setTrainings([]);
        setAttendance([]);
        setLoading(false);
        return;
      }

      let filtered = (trData || []).filter(Boolean);
      if (startBound)
        filtered = filtered.filter((t) => new Date(t.date) >= startBound);
      if (endBound)
        filtered = filtered.filter((t) => new Date(t.date) <= endBound);

      setTrainings(filtered);

      const ids = filtered.map((t) => t.id);
      if (ids.length === 0) {
        setAttendance([]);
        setLoading(false);
        return;
      }

      const { data: attData, error: attErr } = await supabase
        .from("training_attendance")
        .select("training_id, player_id, was_present")
        .in("training_id", ids);

      if (attErr) {
        console.error("Erreur chargement attendance:", attErr);
        setAttendance([]);
      } else {
        setAttendance(attData || []);
      }

      setLoading(false);
    })();
  }, [selectedTeamId, startBound, endBound, teams]);

  /* ================= 3) Calculs ================= */
  const trainingIds = useMemo(
    () => trainings.map((t) => t.id),
    [trainings]
  );

  const perPlayer = useMemo(() => {
    const total = trainings.length;
    return players
      .map((p) => {
        const pid = String(p.id);
        const present = attendance.filter(
          (a) =>
            a.player_id === pid &&
            a.was_present &&
            trainingIds.includes(a.training_id)
        ).length;
        const absent = Math.max(0, total - present);
        const rate = total > 0 ? present / total : 0;
        const name = `${
          p.number != null ? `#${p.number} ` : ""
        }${
          [p.firstName, p.lastName]
            .filter(Boolean)
            .join(" ") || t("attendance:unnamed")
        }`;
        return { id: pid, name, present, absent, total, rate };
      })
      .sort((a, b) => b.rate - a.rate);
  }, [players, attendance, trainingIds, trainings.length, t]);

  const overall = useMemo(() => {
    const presentSum = perPlayer.reduce(
      (acc, p) => acc + p.present,
      0
    );
    const absentSum = perPlayer.reduce(
      (acc, p) => acc + p.absent,
      0
    );
    return {
      data: [
        { name: t("attendance:present"), value: presentSum },
        { name: t("attendance:absent"), value: absentSum },
      ],
    };
  }, [perPlayer, t]);

  // 🔥 Loader global
  if (loading) {
    return <LogoLoader />;
  }

  return (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
    {/* ===== HEADER STICKY ===== */}
    <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] flex items-center gap-3">
        <BackButton fallback="/dashboard" />

        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h1 className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-400">
            {t("attendance:title.main")}
          </h1>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleExportSummaryPdf}
            disabled={!perPlayer || perPlayer.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700
                       bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm disabled:opacity-60"
            title={t("attendance:summary.exportPdf")}
          >
            <FileDown className="w-4 h-4" />
            {t("attendance:summary.export")}
          </button>
        </div>
      </div>
    </div>

    {/* ===== CONTENU ===== */}
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-6">
      {/* Filtres */}
      <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("attendance:filters.team")}
            </label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="border rounded-xl px-3 py-2 w-full
                         bg-white border-gray-300 text-gray-900
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            >
              {teams.map((tm) => (
                <option key={tm.id} value={tm.id}>
                  {tm.team_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("attendance:filters.startDate")}
            </label>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="border rounded-xl px-3 py-2 w-full
                         bg-white border-gray-300 text-gray-900
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("attendance:filters.endDate")}
            </label>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="border rounded-xl px-3 py-2 w-full
                         bg-white border-gray-300 text-gray-900
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="flex items-end">
            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              {t("attendance:filters.trainingsCount", { count: trainings.length })}
            </div>
          </div>
        </div>
      </div>

      {trainings.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-gray-700 dark:text-gray-300">
          {t("attendance:noTrainings")}
        </div>
      ) : (
        <>
          {/* Camembert global */}
          <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
              {teamName} — {t("attendance:charts.globalTitle")}
            </h2>
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={overall.data}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {overall.data.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.name === t("attendance:present") ? COLORS.present : COLORS.absent}
                      />
                    ))}
                  </Pie>

                  <RechartsTooltip
                    contentStyle={{
                      background: isDark ? "#111827" : "#ffffff",
                      borderColor: isDark ? "#374151" : "#e5e7eb",
                      color: isDark ? "#e5e7eb" : "#111827",
                    }}
                    labelStyle={{ color: isDark ? "#e5e7eb" : "#111827" }}
                    itemStyle={{ color: isDark ? "#e5e7eb" : "#111827" }}
                  />
                  <Legend wrapperStyle={{ color: isDark ? "#e5e7eb" : "#111827" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Mini camemberts par joueur */}
          <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">
              {t("attendance:charts.byPlayer")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {perPlayer.map((p) => (
                <div key={p.id} className="border rounded-2xl p-3 dark:border-gray-800">
                  <div className="font-medium mb-2 text-gray-900 dark:text-gray-100">{p.name}</div>
                  <div className="h-48">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={[
                            { name: t("attendance:present"), value: p.present },
                            { name: t("attendance:absent"), value: p.absent },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          <Cell fill={COLORS.present} />
                          <Cell fill={COLORS.absent} />
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{
                            background: isDark ? "#111827" : "#ffffff",
                            borderColor: isDark ? "#374151" : "#e5e7eb",
                            color: isDark ? "#e5e7eb" : "#111827",
                          }}
                          labelStyle={{ color: isDark ? "#e5e7eb" : "#111827" }}
                          itemStyle={{ color: isDark ? "#e5e7eb" : "#111827" }}
                        />
                        <Legend wrapperStyle={{ color: isDark ? "#e5e7eb" : "#111827" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {p.present}/{p.total} {t("attendance:presentLower")} — {(p.rate * 100).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap */}
          <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
              {t("attendance:heatmap.title")}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t("attendance:heatmap.help")}
            </p>

            {weeks.length === 0 ? (
              <div className="text-gray-600 dark:text-gray-400">{t("attendance:heatmap.empty")}</div>
            ) : (
              <div className="overflow-auto">
                <div className="inline-block">
                  <div
                    className="grid"
                    style={{ gridTemplateColumns: `100px repeat(${weeks.length}, 28px)` }}
                  >
                    <div />
                    {weeks.map((w) => (
                      <div
                        key={w.key}
                        className="text-[10px] text-gray-600 dark:text-gray-400 text-center py-1"
                      >
                        {`S${w.isoWeek}`}
                      </div>
                    ))}
                  </div>

                  {WEEKDAYS.map((dayLabel, rowIdx) => (
                    <div
                      key={dayLabel}
                      className="grid"
                      style={{ gridTemplateColumns: `100px repeat(${weeks.length}, 28px)` }}
                    >
                      <div className="text-xs text-gray-700 dark:text-gray-300 py-1 pr-2 flex items-center">
                        {dayLabel}
                      </div>

                      {weeks.map((w) => {
                        const cell = heatCells.find((col) => col[0]?.weekLabel === `S${w.isoWeek}`)?.[rowIdx];
                        const bg = valueToColor(cell?.value ?? null);

                        const title =
                          (cell?.value ?? null) === null
                            ? `${dayLabel} — ${t("attendance:heatmap.noSession")}`
                            : `${dayLabel} ${cell?.dateLabel || ""} — ${((cell?.value || 0) * 100).toFixed(0)}% ${t("attendance:presentLower")}`;

                        return (
                          <div
                            key={`${w.key}-${rowIdx}`}
                            title={title}
                            className="h-6 w-7 rounded-sm border border-black/10 dark:border-white/10"
                            style={{ backgroundColor: bg }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 mt-4 text-xs text-gray-600 dark:text-gray-400">
                  <span>0%</span>
                  <div
                    className="h-3 w-28 rounded"
                    style={{
                      background: `linear-gradient(to right, ${isDark ? "#111827" : "#e5e7eb"}, hsl(130, 70%, 35%))`,
                    }}
                  />
                  <span>100%</span>
                </div>
              </div>
            )}
          </div>

          {/* Tableau synthèse */}
          <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 dark:bg-gray-900 dark:border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                {t("attendance:summary.title")}
              </h2>
              <button
                onClick={handleExportSummaryPdf}
                className="px-2 py-1 rounded border inline-flex items-center gap-2 text-sm
                           border-gray-300 hover:bg-gray-50
                           dark:border-gray-700 dark:hover:bg-gray-800"
                title={t("attendance:summary.exportPdf")}
              >
                <FileDown className="w-4 h-4" />
                {t("attendance:summary.export")}
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2 pr-4 text-gray-700 dark:text-gray-300">{t("attendance:table.player")}</th>
                    <th className="py-2 pr-4 text-gray-700 dark:text-gray-300">{t("attendance:table.present")}</th>
                    <th className="py-2 pr-4 text-gray-700 dark:text-gray-300">{t("attendance:table.absent")}</th>
                    <th className="py-2 pr-4 text-gray-700 dark:text-gray-300">{t("attendance:table.rate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {perPlayer.map((p) => (
                    <tr key={p.id} className="border-b last:border-b-0 border-gray-200 dark:border-gray-800">
                      <td className="py-2 pr-4">{p.name}</td>
                      <td className="py-2 pr-4">{p.present}</td>
                      <td className="py-2 pr-4">{p.absent}</td>
                      <td className="py-2 pr-4">{(p.rate * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);
}
